import React, { useEffect, useRef, useCallback, useState } from 'react';
import { snapTo90Degrees } from '../utils/geometry';
import { FEET_TO_METERS, METERS_TO_FEET, formatMeasurement } from '../utils/conversion';
import { Modal, Button, Input, Form, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

/**
 * ScaleTool - Component for setting and displaying scale
 * 
 * @param {Object} props
 * @param {string} props.renderType - Type of rendering ('svg' or 'panel')
 * @param {boolean} props.isSettingScale - Whether scale setting mode is active
 * @param {Array} props.measurePoints - Array of measurement points
 * @param {string} props.inchesValue - Value in inches/feet input field
 * @param {Object} props.referenceLine - Reference line object with points and distance
 * @param {Function} props.transformPoint - Function to transform points to screen coordinates (not used when SVG is inside transformed container)
 * @param {Function} props.handleInchesChange - Function to handle inches input change
 * @param {Function} props.applyScale - Function to apply the scale
 * @param {number} props.FEET_TO_METERS - Conversion factor from feet to meters
 * @param {number} props.METERS_TO_FEET - Conversion factor from meters to feet
 * @param {number} props.pixelsPerFoot - Current scale in pixels per foot
 * @param {number} props.measureDistance - Current measurement distance in pixels
 * @param {number} props.zoom - Current zoom level (for adjusting element sizes)
 * @param {boolean} props.shiftPressed - Whether the shift key is pressed
 * @param {Object} props.currentMousePos - Current mouse position for preview
 * @param {boolean} props.active - Whether the modal is active
 * @param {Function} props.onCancel - Function to handle cancel
 * @param {Function} props.onFinish - Function to handle finish
 * @param {Function} props.setInchesValue - Function to set inches value
 * @returns {JSX.Element} ScaleTool component
 */
const ScaleTool = ({ 
  renderType = 'svg',
  isSettingScale, 
  measurePoints, 
  inchesValue, 
  referenceLine, 
  transformPoint, 
  handleInchesChange, 
  applyScale,
  pixelsPerFoot,
  measureDistance,
  zoom = 1,
  shiftPressed = false,
  currentMousePos = null,
  FEET_TO_METERS,
  METERS_TO_FEET
}) => {
  // Reference to the input field for auto-focus
  const inputRef = useRef(null);
  
  // Local state to track input value
  const [localInchesValue, setLocalInchesValue] = useState(inchesValue || '');
  
  // Update local state when prop changes
  useEffect(() => {
    if (inchesValue !== undefined && inchesValue !== null) {
      setLocalInchesValue(inchesValue);
    }
  }, [inchesValue]);

  // Add some CSS for the modal to ensure it's centered
  useEffect(() => {
    // Add custom CSS for the modal
    const style = document.createElement('style');
    style.innerHTML = `
      .ant-modal {
        top: 50%;
        transform: translateY(-50%);
      }
      .scale-tool-content {
        text-align: center;
      }
      .measurement-input {
        max-width: 500px;
        margin: 0 auto;
      }
      .button-row {
        display: flex;
        justify-content: center;
        gap: 10px;
        margin-top: 15px;
      }
      .help-text {
        text-align: left;
        margin: 15px 0;
      }
      .preview {
        font-weight: bold;
        color: #1890ff;
      }
      .current-scale {
        margin: 15px 0;
      }
      .math-explanation {
        text-align: left;
        background-color: #f5f5f5;
        padding: 10px;
        border-radius: 4px;
        font-size: 13px;
        color: #444;
        margin-top: 10px;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Auto-focus the input field when the second point is created
  useEffect(() => {
    if (isSettingScale && measurePoints.length === 2 && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSettingScale, measurePoints.length]);

  // Ensure focus anytime the panel is visible
  useEffect(() => {
    // Small delay to ensure the DOM is ready
    const timer = setTimeout(() => {
      if (isSettingScale && measurePoints.length === 2 && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 10);
    
    return () => clearTimeout(timer);
  }, [isSettingScale, measurePoints.length, inputRef.current]);
  
  // Create reference for panel element - keep this for styling only
  const panelRef = useRef(null);
  
  // Handle ESC key to cancel scale operation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isSettingScale && measurePoints.length === 2) {
        if (typeof applyScale === 'function') {
          // Cancel by sending null
          applyScale(null);
        }
      }
    };

    if (isSettingScale) {
      window.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSettingScale, measurePoints, applyScale]);

  // Local handler for inches value changes
  const handleLocalInchesChange = useCallback((e) => {
    const value = e.target.value;
    setLocalInchesValue(value);
    
    // Update parent component if handler is provided
    if (typeof handleInchesChange === 'function') {
      handleInchesChange(e);
    }
  }, [handleInchesChange]);

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && localInchesValue && !isNaN(localInchesValue) && parseFloat(localInchesValue) > 0) {
      handleScaleSet();
    }
  };

  // Render reference line (always visible with 50% opacity)
  const renderReferenceLine = () => {
    if (!referenceLine) return null;
    
    // When in scale setting mode, make the reference line more subtle/faded
    const opacity = isSettingScale ? 0.3 : 0.5;
    const strokeDashArray = isSettingScale ? `${8 / zoom},${5 / zoom}` : `${5 / zoom},${5 / zoom}`;
    
    // The distance is now stored directly in feet in the referenceLine object
    let displayValue = '0\'0"';
    if (referenceLine.distance && !isNaN(referenceLine.distance)) {
      // Use the distance directly (already in feet)
      displayValue = formatMeasurement(referenceLine.distance);
    }
    
    // Directly use untransformed points when SVG is inside the transformed container
    return (
      <>
        <circle 
          cx={referenceLine.points[0].x} 
          cy={referenceLine.points[0].y} 
          r={5 / zoom} 
          fill={`rgba(255, 0, 0, ${opacity})`} 
        />
        <line 
          x1={referenceLine.points[0].x} 
          y1={referenceLine.points[0].y} 
          x2={referenceLine.points[1].x} 
          y2={referenceLine.points[1].y} 
          stroke={`rgba(255, 0, 0, ${opacity})`} 
          strokeWidth={2 / zoom} 
          strokeDasharray={strokeDashArray}
        />
        <circle 
          cx={referenceLine.points[1].x} 
          cy={referenceLine.points[1].y} 
          r={5 / zoom} 
          fill={`rgba(255, 0, 0, ${opacity})`} 
        />
        <text
          x={(referenceLine.points[0].x + referenceLine.points[1].x) / 2}
          y={(referenceLine.points[0].y + referenceLine.points[1].y) / 2 - 10 / zoom}
          fill={`rgba(255, 0, 0, ${opacity + 0.1})`}
          textAnchor="middle"
          fontSize={12 / zoom}
          fontWeight="bold"
        >
          {displayValue}
        </text>
      </>
    );
  };

  // Render current measurement points
  const renderMeasurementPoints = () => {
    if (measurePoints.length === 0) return null;
    
    // Directly use untransformed points when SVG is inside the transformed container
    let secondPoint = measurePoints.length > 1 ? measurePoints[1] : null;
    
    // Preview with current mouse position if we only have one point and are actively measuring
    if (measurePoints.length === 1 && isSettingScale && currentMousePos) {
      // Use snapped position if shift is pressed
      if (shiftPressed) {
        // Calculate angle between points
        const dx = currentMousePos.x - measurePoints[0].x;
        const dy = currentMousePos.y - measurePoints[0].y;
        const angle = Math.atan2(dy, dx);
        
        // Snap to nearest 90-degree angle
        const snappedAngle = Math.round(angle / (Math.PI / 2)) * (Math.PI / 2);
        
        // Calculate new point at snapped angle
        const distance = Math.sqrt(dx * dx + dy * dy);
        secondPoint = {
          x: measurePoints[0].x + Math.cos(snappedAngle) * distance,
          y: measurePoints[0].y + Math.sin(snappedAngle) * distance
        };
      } else {
        secondPoint = currentMousePos;
      }
    }
    
    return (
      <>
        {/* Draw the first point */}
        <circle 
          cx={measurePoints[0].x} 
          cy={measurePoints[0].y} 
          r={5 / zoom} 
          fill="red" 
        />
        
        {/* Draw the line and second point if available */}
        {secondPoint && (
          <>
            <line 
              x1={measurePoints[0].x} 
              y1={measurePoints[0].y} 
              x2={secondPoint.x} 
              y2={secondPoint.y} 
              stroke="red" 
              strokeWidth={2 / zoom} 
            />
            <circle 
              cx={secondPoint.x} 
              cy={secondPoint.y} 
              r={5 / zoom} 
              fill="red" 
            />
            {/* Only show measurement text when both points are actually placed (not during drawing) */}
            {measureDistance && measurePoints.length === 2 && (
              <text
                x={(measurePoints[0].x + secondPoint.x) / 2}
                y={(measurePoints[0].y + secondPoint.y) / 2 - 10 / zoom}
                fill="red"
                textAnchor="middle"
                fontSize={12 / zoom}
                fontWeight="bold"
              >
                {(() => {
                  // Safely calculate and format the measurement
                  if (!pixelsPerFoot || pixelsPerFoot <= 0 || isNaN(pixelsPerFoot)) {
                    return ''; // Don't show any text if scale isn't set
                  }
                  
                  // Calculate feet value and display it
                  const feet = measureDistance / pixelsPerFoot;
                  if (isNaN(feet) || !isFinite(feet)) {
                    console.error('Invalid feet calculation:', feet);
                    return 'Invalid';
                  }
                  return formatMeasurement(feet);
                })()}
              </text>
            )}
          </>
        )}
      </>
    );
  };

  // Render scale setting panel
  const renderScaleSettingPanel = () => {
    if (!isSettingScale) return null;
    
    // If we don't have two points yet, don't render the panel
    if (measurePoints.length < 2) {
      return null;
    }
    
    return (
      <div className="scale-setting-panel" ref={panelRef}>
        <div className="scale-panel-title">
          Set the length of the red line:
        </div>
        
        <div className="scale-input-group">
          <input 
            ref={inputRef}
            type="text" 
            className="scale-input" 
            placeholder="Enter length in feet (example: 6.25 for 6 feet 3 inches)" 
            value={localInchesValue} 
            onChange={handleLocalInchesChange}
            onKeyPress={handleKeyPress}
          />
          <span className="scale-unit">feet</span>
        </div>
        
        <button 
          className="scale-apply-button" 
          onClick={handleScaleSet}
          disabled={!localInchesValue || isNaN(localInchesValue) || parseFloat(localInchesValue) <= 0}
        >
          Apply Scale
        </button>
        <div className="scale-info-text">
          <p>Press Enter to apply or Esc to cancel</p>
        </div>
      </div>
    );
  };

  // Render scale instructions
  const renderScaleInstructions = () => {
    if (!isSettingScale) return null;
    
    if (measurePoints.length < 2) {
      return (
        <div className="measure-instructions">
          {measurePoints.length === 0 
            ? "Click two points to measure a known distance 📐" 
            : "Click to set the second point 📐"}
        </div>
      );
    }
    
    return null;
  };

  // Convert input value to feet
  const getInputDistanceInFeet = useCallback(() => {
    if (!localInchesValue) return 0;
    
    // Try to parse as a number first
    let parsedValue = parseFloat(localInchesValue);
    
    if (isNaN(parsedValue)) {
      // If parsing fails, try to evaluate as a mathematical expression
      try {
        parsedValue = eval(localInchesValue);
      } catch (error) {
        console.error("Invalid expression:", error);
        return 0;
      }
    }
    
    // Return feet directly since input is now in feet
    return parsedValue;
  }, [localInchesValue]);

  // Calculate and update the pixelsPerFoot value
  const handleScaleSet = useCallback(() => {
    // Get the distance in feet from the input
    const distanceInFeet = getInputDistanceInFeet();
    
    if (distanceInFeet <= 0 || !measureDistance) {
      console.error("Invalid distance or no measurement");
      return;
    }
    
    // Calculate pixelsPerFoot based on the measured pixel distance and input feet
    const newPixelsPerFoot = measureDistance / distanceInFeet;
    
    // Validate result before proceeding
    if (isNaN(newPixelsPerFoot) || !isFinite(newPixelsPerFoot) || newPixelsPerFoot <= 0) {
      console.error("Invalid scale calculation result:", {
        measureDistance,
        distanceInFeet,
        newPixelsPerFoot
      });
      return;
    }
    
    // Update the parent component with the input value
    if (typeof handleInchesChange === 'function') {
      handleInchesChange({ target: { value: localInchesValue } });
    }
    
    // Parent will now expect any non-null value to continue with scale setting
    // The actual pixelsPerFoot calculation happens in the parent
    applyScale('apply');
  }, [getInputDistanceInFeet, measureDistance, applyScale, localInchesValue, handleInchesChange]);

  // Render based on the renderType
  if (renderType === 'svg') {
    return (
      <g className="scale-tool">
        {renderReferenceLine()}
        {renderMeasurementPoints()}
      </g>
    );
  } else if (renderType === 'panel') {
    return (
      <>
        {renderScaleInstructions()}
        {renderScaleSettingPanel()}
      </>
    );
  }
  
  return (
    <Modal
      title="Set Scale"
      open={isSettingScale}
      onCancel={() => applyScale(null)}
      footer={null}
      destroyOnClose={true}
    >
      <div className="scale-tool-content">
        {measurePoints.length === 0 && (
          <div className="instructions">
            <p>Click on the floorplan to place the start point of a reference line.</p>
          </div>
        )}
        
        {measurePoints.length === 1 && (
          <div className="instructions">
            <p>Click to place the end point of the reference line.</p>
          </div>
        )}
        
        {measurePoints.length === 2 && (
          <div className="measurement-input">
            <p>You've drawn a line of {measureDistance.toFixed(1)} pixels.</p>
            <p>Enter the real-world length of this line:</p>
            
            <Form layout="vertical">
              <Form.Item
                label="Length in feet"
                tooltip={{ 
                  title: 'Enter the real-world length in feet. You can use formulas like "6.25" for 6\' 3".', 
                  icon: <InfoCircleOutlined /> 
                }}
              >
                <Input
                  placeholder="Enter length in feet (example: 6.25 for 6 feet 3 inches)"
                  value={localInchesValue}
                  onChange={handleLocalInchesChange}
                  suffix="feet"
                />
              </Form.Item>
              
              <div className="help-text">
                <p><b>Tips:</b></p>
                <ul>
                  <li>Use wall lengths, doorways, or furniture with known dimensions</li>
                  <li>You can use decimal values like "6.25" for 6 feet 3 inches</li>
                  <li>For best results, use the longest reference line possible</li>
                </ul>
              </div>
              
              {localInchesValue && (
                <p className="preview">
                  This is {parseFloat(localInchesValue).toFixed(2)} feet
                </p>
              )}
              
              <div className="current-scale">
                <p>Current scale: {pixelsPerFoot.toFixed(2)} pixels/foot</p>
                <p className="math-explanation">
                  <b>Square footage calculation:</b><br/>
                  1. Measure box dimensions in pixels<br/>
                  2. Convert to feet: width / pixelsPerFoot, height / pixelsPerFoot<br/>
                  3. Calculate area: widthInFeet × heightInFeet<br/>
                  4. For example: 150px × 100px box with scale of 30px/ft = (150/30) × (100/30) = 5ft × 3.33ft = 16.67 sqft
                </p>
              </div>
              
              <div className="button-row">
                <Button onClick={() => applyScale(null)}>Cancel</Button>
                <Button type="primary" onClick={handleScaleSet} disabled={!localInchesValue}>
                  Set Scale
                </Button>
              </div>
            </Form>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ScaleTool; 